import { Button, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeContext } from '@/Contexts/PartnerIntakeContext';
import { TokenContext } from '@/Contexts/TokenContext';
import useIntake from '@/hooks/useIntake';
import { ContactProps } from '@/models/Contact';
import { IntakeFormProps } from '@/models/IntakeForm';
import { PartnerProps } from '@/models/Partner';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';
import { formatErrorMessage } from '@/utils/errorHandlerUtil';

import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { IntakeResponse } from '@/models/IntakeResponse';
import IntakeErrorModal from './IntakeErrorModal';

interface IntakeSubmitButtonProps {
  onSubmit: (response: IntakeResponse) => void;
}

function IntakeSubmitButton({ onSubmit }: IntakeSubmitButtonProps) {
  const { postIntake } = useIntake();

  const { translatedText } = useContext(LocaleContext);
  const { allCountries } = useContext(ConfigContext);
  const { b2cId } = useContext(TokenContext);
  const { setAlert } = useContext(SnackbarContext);

  const {
    partnerInfo,
    partnerGroupInfo,
    isNewPartner,
    primaryContact,
    shippingContact,
    intakeHasErrors,
    selectedProgramInfo,
    updateShowErrors,
  } = useContext(PartnerIntakeContext);

  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);

  function handleModalClose() {
    setOpenModal(false);
  }

  async function handleSubmit() {
    if (intakeHasErrors) {
      updateShowErrors(true);
      setTimeout(() => {
        setOpenModal(true);
      }, 500);
    } else {
      setSubmitInProgress(true);
      const verifiedPrimaryContactCountryId =
        primaryContact?.countryId != null &&
        allCountries.find((country) => country.id == primaryContact?.countryId) != null
          ? primaryContact.countryId
          : USCountryId;

      const cleanContactInfo: ContactProps = {
        ...primaryContact,
        departmentTypeId: primaryContact?.departmentTypeId,
        contactSubTypeId: primaryContact?.contactSubTypeId,
        countryId: [ProgramNames.TreeCitiesWorld].includes(selectedProgramInfo.id)
          ? verifiedPrimaryContactCountryId
          : USCountryId,
      };

      const verifiedPartnerCountryId =
        allCountries.find((country) => country.id == partnerInfo?.countryId) != null
          ? partnerInfo.countryId
          : USCountryId;

      const cleanPartnerInfo: PartnerProps = {
        ...partnerInfo,
        programId: selectedProgramInfo.id,
        countryId: [ProgramNames.TreeCitiesWorld].includes(selectedProgramInfo.id)
          ? verifiedPartnerCountryId
          : USCountryId,
      };

      const postInfo: IntakeFormProps = {
        b2cid: b2cId,
        contact: cleanContactInfo,
        shippingContact: isNewPartner && shippingContact != null ? shippingContact : null,
        partnerGroup: !isNewPartner ? { id: Number(partnerGroupInfo?.id) } : partnerGroupInfo,
        partner: !isNewPartner ? { id: Number(partnerInfo.id) } : cleanPartnerInfo,
      };

      try {
        const intakeResponse = await postIntake(postInfo);

        if (intakeResponse != null && 'errors' in intakeResponse) {
          setAlert({
            severity: 'error',
            message: `${translatedText.signup.messageSubmissionError} ${formatErrorMessage(intakeResponse)}`,
          });
          setSubmitInProgress(false);
        } else if (intakeResponse?.message == 'Intake form successfully submitted') {
          onSubmit(intakeResponse);
        } else {
          setAlert({
            severity: 'error',
            message: translatedText.signup.messageSubmissionError,
          });
        }
      } catch {
        setAlert({
          severity: 'error',
          message: translatedText.signup.messageSubmissionError,
        });
      }

      setSubmitInProgress(false);
    }
  }

  return (
    <>
      <IntakeErrorModal open={openModal} onClose={handleModalClose} />

      <Tooltip title={translatedText.signup.tooltipSubmit}>
        <Button onClick={handleSubmit} disabled={submitInProgress} variant='primary' size='large'>
          {submitInProgress
            ? translatedText.applicationBody.submitMessage
            : translatedText.signup.buttonSubmit}
        </Button>
      </Tooltip>
    </>
  );
}

export default IntakeSubmitButton;
